import * as React from "react";
import { Table } from "react-bootstrap";
import { Button, InlineNotification } from "carbon-components-react";
import { Save16, Renew16, DocumentView24 } from "@carbon/icons-react";
import { connect } from "react-redux";
import Select from "react-select";

import BorrowerEntityUtil, {
  borrowerCountFromApp,
  borrowerEntitySelectOptionsFromApp,
} from "@eave/shared/utils/borrowers";
import { AppShape, BorrowerEntity, CreditReport, HurdleShape } from "@eave/shared/types/api";
import LabeledInput from "web_modules/LabeledInput";
import HurdleView from "underwriter_dashboard/shared/HurdleView";
import APIClient from "../../APIClient";
import { getFileDownloadBaseURL } from "../../../config";
import { loadApp } from "../actions";
import { pullMultipleCreditReportsIsEnabled } from "web_modules/shared/feature-flag";
import { getBorrowerNamesAsCsv } from "@eave/shared/utils/credit-reports";

const creditReportToUrl = cr => getFileDownloadBaseURL(cr.pdf_s3_key);
const styles = require("./styles.module.scss");

require(`../../../assets/stylesheets/components/underwriter_dashboard/hurdle_editors/credit_reports.scss`);

type ReportNotificationProps = {
  creditReport: CreditReport;
};

const ReportNotification = ({ creditReport }: ReportNotificationProps): JSX.Element => {
  const { status, id } = creditReport;

  if (!status) return null;

  // currently the format of the status response doesn't allow parse it, so in order to get the description
  // the string is splitted and cleaned in this way
  const [ErrorNumber, description] = status.replace(/\[|\]|,/g, "").split("Description:");

  const inlineNotificationDetails = {
    title: `Error: CreditReport ${id} (${ErrorNumber})`,
    subtitle: `has the following error: ${description}`,
  };

  return <InlineNotification kind="error" {...inlineNotificationDetails} />;
};

interface CreditReportPullProps {
  dispatch: Function;
  app: AppShape;
  hurdle: HurdleShape;
  onHide: Function;
}

class CreditReportPull extends React.Component<CreditReportPullProps, any> {
  constructor(props) {
    super(props);
    this.state = CreditReportPull.initialState(props);
  }

  static initialState(props) {
    const { credit_reports } = props.app;

    if (credit_reports.length === 0) {
      return {};
    }

    return {
      crs_operator_confirmed_ok: this.initialCreditReportsState(credit_reports),
      joint_cr_be_1: 0,
      joint_cr_be_2: 0,
    };
  }

  static initialCreditReportsState(credit_reports: CreditReport[]) {
    const crs_operator_confirmed_ok = new Map();
    for (let i = 0; i < credit_reports.length; i++) {
      const cr = credit_reports[i];
      crs_operator_confirmed_ok.set(cr.id, cr.operator_confirmed_ok);
    }

    return crs_operator_confirmed_ok;
  }

  reloadCreditReport(borrower_entity: BorrowerEntity = BorrowerEntity.BorrowerNone) {
    let api_url = `/${this.props.app.id}/reload_credit_report`;
    if (borrower_entity !== BorrowerEntity.BorrowerNone) {
      api_url += `/${borrower_entity}`;
    }

    if (
      confirm(`WARNING! This will overwrite any changes and overrides made on the \
        CreditSummaryHurdle & in the CreditScorePricingHurdle`)
    ) {
      APIClient.get(api_url).then(() => {
        loadApp(this.props.dispatch, this.props.app.id);
      });
    }
  }

  valueEdited() {
    const { crs_operator_confirmed_ok } = this.state;
    const { credit_reports } = this.props.app;

    for (let i = 0; i < credit_reports.length; i++) {
      const cr = credit_reports[i];
      if (crs_operator_confirmed_ok.get(cr.id) !== cr.operator_confirmed_ok) {
        return true;
      }
    }

    return false;
  }

  onSave() {
    const { dispatch, app, onHide } = this.props;
    const { crs_operator_confirmed_ok } = this.state;

    const params = Array.from(crs_operator_confirmed_ok, ([id, operator_confirmed_ok]) => ({
      id,
      operator_confirmed_ok,
    }));

    APIClient.put(
      `/loan_applications/${app.id}/hurdles/credit_report/toggle_confirmed`,
      params,
    ).then(() => {
      loadApp(dispatch, app.id);
    });

    onHide();
  }

  onChange(id: number, e: any) {
    const crs_operator_confirmed_ok = this.state.crs_operator_confirmed_ok;
    if (crs_operator_confirmed_ok.get(id) === e.value) {
      return;
    }

    this.setState({
      crs_operator_confirmed_ok: new Map(crs_operator_confirmed_ok).set(id, e.value),
    });
  }

  renderCreditReportsTable(credit_reports: CreditReport[]) {
    const NotificationsReports = () => (
      <>
        {credit_reports.map((creditReport: CreditReport) => (
          <ReportNotification creditReport={creditReport} />
        ))}
      </>
    );

    return (
      <>
        <h3>Credit Reports</h3>
        <NotificationsReports />
        <Table striped>
          <thead>
            <tr>
              <th>Borrowers</th>
              <th>View</th>
              <th>Status</th>
              <th>Actions</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {credit_reports.map(cr => (
              <tr key={cr.id}>
                <td>{getBorrowerNamesAsCsv(cr)}</td>
                <td>{this.renderLink(cr)}</td>
                <td>
                  {cr.needs_operator_attention ? (
                    <span>needs operator attention before proceeding</span>
                  ) : (
                    <span>no obvious errors or fraud warnings</span>
                  )}
                </td>
                <td>
                  {cr.needs_operator_attention ? (
                    this.renderSelect(cr.id)
                  ) : (
                    <span className="hurdles-editor__emdash">—</span>
                  )}
                </td>
                <td>
                  <Button
                    kind="tertiary"
                    renderIcon={Renew16}
                    onClick={() => this.reloadCreditReport(cr.borrower_entity)}
                  >
                    Request new credit report
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </>
    );
  }

  renderLink(credit_report: CreditReport) {
    return (
      <div>
        <a href={creditReportToUrl(credit_report)} target="_blank">
          <DocumentView24 />
        </a>
      </div>
    );
  }

  renderSelect(id: number) {
    const opts = [
      { value: null, label: <span className="hurdles-editor__emdash">—</span> },
      { value: false, label: "No, block borrower from continuing" },
      { value: true, label: "Yes, let borrower proceed" },
    ];

    return (
      <div style={{ width: 300 }}>
        <Select
          placeholder="Does this credit report look OK?"
          clearable={false}
          searchable={false}
          value={this.state.crs_operator_confirmed_ok.get(id)}
          options={opts}
          onChange={e => this.onChange(id, e)}
        />
      </div>
    );
  }

  renderPullCreditReports() {
    return (
      <div style={{ marginTop: "10px" }}>
        <h3>Pull Credit Reports</h3>
        <div style={{ marginTop: "10px" }}>
          <Button onClick={() => this.reloadCreditReport()} renderIcon={Renew16}>
            Request new credit reports
          </Button>
        </div>
      </div>
    );
  }

  getJointBorrowersEntity = (be1, be2) => new BorrowerEntityUtil(be1).with(be2).value();

  getBorrowerOptions(app: AppShape, valueFilter: number = 0) {
    return borrowerEntitySelectOptionsFromApp(app).filter(option => option.value !== valueFilter);
  }

  handleJointCRSelectChange = (field, e) => this.setState({ [field]: e ? e.value : 0 });

  renderPullJointCreditReports() {
    const { app } = this.props;
    const { joint_cr_be_1, joint_cr_be_2 } = this.state;

    return (
      <>
        <h3>Request Joint Credit Report</h3>
        <div className="pull-joint-credit-reports">
          <LabeledInput
            placeholder="Borrower 1"
            inputType="select"
            inputName="joint_cr_be_1"
            inputValue={joint_cr_be_1}
            options={this.getBorrowerOptions(app, joint_cr_be_2)}
            onChange={e => this.handleJointCRSelectChange("joint_cr_be_1", e)}
          />
          <LabeledInput
            placeholder="Borrower 2"
            inputType="select"
            inputName="joint_cr_be_2"
            inputValue={joint_cr_be_2}
            options={this.getBorrowerOptions(app, joint_cr_be_1)}
            onChange={e => this.handleJointCRSelectChange("joint_cr_be_2", e)}
          />
        </div>
        <div style={{ marginTop: "40px" }}>
          <Button
            onClick={() =>
              this.reloadCreditReport(this.getJointBorrowersEntity(joint_cr_be_1, joint_cr_be_2))
            }
            disabled={joint_cr_be_1 === 0 || joint_cr_be_2 === 0}
          >
            Request Joint Credit Report
          </Button>
        </div>
      </>
    );
  }

  render() {
    const { app, hurdle, onHide } = this.props;
    const { credit_reports } = app;
    const content = [];

    if (credit_reports.length === 0) {
      content.push(
        <>
          Credit reports not pulled yet
          {this.renderPullCreditReports()}
        </>,
      );
    } else {
      content.push(<>{this.renderCreditReportsTable(credit_reports)}</>);
      if (pullMultipleCreditReportsIsEnabled() && borrowerCountFromApp(app) > 1) {
        content.push(<>{this.renderPullJointCreditReports()}</>);
      }
    }

    let footer: any;
    if (this.valueEdited()) {
      footer = (
        <>
          <Button kind="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button onClick={this.onSave.bind(this)} renderIcon={Save16}>
            Save changes
          </Button>
        </>
      );
    } else {
      footer = <Button onClick={onHide}>OK</Button>;
    }

    return (
      <HurdleView app={app} hurdle={hurdle} onHide={onHide}>
        <div className="dashboard-container">
          {content}
          <footer className={styles.footer}>{footer}</footer>
        </div>
      </HurdleView>
    );
  }
}

export default connect()(CreditReportPull);
